import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import Login from "./pages/Login";
import Index from "./pages/Index";
import CandidatesPage from "./pages/Candidates";
import InterviewsPage from "./pages/Interviews";
import TasksPage from "./pages/Tasks";
import ShortlistedPage from "./pages/recruitment/Shortlisted";
import InterviewedPage from "./pages/recruitment/Interviewed";
import HiredPage from "./pages/recruitment/Hired";
import SettingsPage from "./pages/Settings";
import NotFound from "./pages/NotFound";

// Candidate pages
import CandidateDashboard from "./pages/candidate/Dashboard";
import CandidateProfile from "./pages/candidate/Profile";
import CandidateApplications from "./pages/candidate/Applications";
import CandidateNotifications from "./pages/candidate/Notifications";

const queryClient = new QueryClient();

// Protected route wrapper
const ProtectedRoute = ({ 
  children, 
  allowedRole 
}: { 
  children: React.ReactNode; 
  allowedRole?: 'company' | 'candidate';
}) => {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRole && user?.role !== allowedRole) {
    return <Navigate to={user?.role === 'company' ? '/' : '/candidate/dashboard'} replace />;
  }

  return <>{children}</>;
};

// Public route - redirect if authenticated
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={user?.role === 'company' ? '/' : '/candidate/dashboard'} replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Route */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      {/* Company Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute allowedRole="company">
            <Index />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidates"
        element={
          <ProtectedRoute allowedRole="company">
            <CandidatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviews"
        element={
          <ProtectedRoute allowedRole="company">
            <InterviewsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute allowedRole="company">
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recruitment/shortlisted"
        element={
          <ProtectedRoute allowedRole="company">
            <ShortlistedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recruitment/interviews"
        element={
          <ProtectedRoute allowedRole="company">
            <InterviewedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recruitment/hired"
        element={
          <ProtectedRoute allowedRole="company">
            <HiredPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRole="company">
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Candidate Routes */}
      <Route
        path="/candidate/dashboard"
        element={
          <ProtectedRoute allowedRole="candidate">
            <CandidateDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidate/profile"
        element={
          <ProtectedRoute allowedRole="candidate">
            <CandidateProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidate/applications"
        element={
          <ProtectedRoute allowedRole="candidate">
            <CandidateApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidate/applications/:status"
        element={
          <ProtectedRoute allowedRole="candidate">
            <CandidateApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidate/notifications"
        element={
          <ProtectedRoute allowedRole="candidate">
            <CandidateNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidate/settings"
        element={
          <ProtectedRoute allowedRole="candidate">
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AppRoutes />
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
